import { getAppointmentTypeFromAperoLink } from "@9amhealth/shared/src/lib/aperoSchedulerMapping";
import { Blac, Cubit } from "blac-next";
import { isAperoLink } from "lib/Urls";
import type { NavigateFunction, NavigateOptions, To } from "react-router-dom";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { appStatusBarState, toast, tracker } from "src/state/state";

export enum AppPopup {
  scheduleAppointment = "schedule",
  medicalInsurance = "medical-insurance",
  rpmDailyReadings = "dailyReadings",
  uploadLabReport = "uploadLabReport",
  enterBloodGlucose = "enterBloodGlucose",
  enterWaistCircumference = "enterWaistCircumference",
  enterBloodPressure = "enterBloodPressure",
  enterWeight = "enterWeight",
  profileSelectPCP = "profileSelectPCP",
  profileSelectPharmacy = "profileSelectPharmacy",
  profileEnterPharmacyInsurance = "profileEnterPharmacyInsurance",
  questionnaire = "questionnaire",
  iframe = "iframe",
  article = "article",
  teamDetails = "teamDetails",
  healthSyncSetup = "healthSyncSetup",
  lifestyleProgram = "lifestyleProgram",
  appointment = "appointment",
  initialLab = "initialLab",
  recurringLabQuestionnaire = "recurringLabQuestionnaire",
  labInstructions = "labInstructions",
  sanityContent = "sanityContent",
  onboardingTasks = "onboardingTasks",
  fastPass = "fastPass",
  glp1StartDate = "glp1StartDate",
  glp1Refill = "refillGlp1Survey",
  prepareRefill = "prepareRefill",
  behavioralHealth = "behavioralHealth",
  chooseYourNextJourney = "chooseYourNextJourney",
  faq = "faq",
  glpContinuation = "glpContinuation",
  prescriptionLabelUpload = "prescriptionLabelUpload",
  weightProofUpload = "weightProofUpload",
  sessionConfirmation = "sessionConfirmation",
  rateCoachingCall = "rateCoachingCall",
  consistencyScoreInfo = "consistencyScoreInfo",
  stepOnScale = "stepOnScale",
  notifications = "notifications"
}

export const AppPopupTypesSet = new Set<AppPopup>(
  new Array(...Object.values(AppPopup))
);

export type ReservedQueryParameters = {
  /**
   * set the z-index of the popup
   */
  z?: string;

  /**
   * if set to 'false', the popup will only open if it was triggered with the `.openPopup` method.
   * If the user reloads the page, the popup will not open.
   * @default 'true'
   */
  stay?: "false" | "true";
};

export type AppQueryPopupOpenOptions = {
  replace?: boolean;
  zIndex?: number;
  additionalParameters?: Record<string, string> & ReservedQueryParameters;
  onEvent?: Record<string | "popupClosed", () => void>;
};

type AppQueryPopupsCallbackType = "onClose";
type AppQueryPopupsCallbackMethod = (
  query: AppPopup | string | null
) => unknown;

type AppQueryPopupsState = {
  popup: AppPopup | null;
};

export default class AppQueryPopupsBloc extends Cubit<AppQueryPopupsState> {
  static keepAlive = true;
  navigate: NavigateFunction | null = null;
  openedByMethod: AppPopup | null = null;

  callbacks: Record<
    AppQueryPopupsCallbackType,
    AppQueryPopupsCallbackMethod[]
  > = {
    onClose: []
  };

  constructor() {
    super({ popup: null });
  }

  setNavigate = (navigate: NavigateFunction) => {
    this.navigate = navigate;
  };

  useNavigate = (to: To, options?: NavigateOptions) => {
    if (!this.navigate) {
      reportErrorSentry(new Error("navigate is not set"));
      toast.error("errorPage.loading");
      return;
    }
    return this.navigate(to, options);
  };

  eventListeners: { event: string; callback: (() => void) | undefined }[] = [];

  /**
   * Rewrites the given params object to prepare for in-app scheduler redirection.
   *
   * If `id` is present, sets `reschedule` to the value of `id`, removes `id`, and sets the popup type.
   * If `url` is present, decodes the URL, sets `type` using getAppointmentTypeFromAperoLink, and sets the popup type.
   * If neither is present, reports an error to Sentry.
   *
   * @param allParams - All query parameters from the URL, mutated in place.
   */
  private rewriteToScheduler = (
    allParams: Record<string, string | undefined>
  ) => {
    let rewriteSuccess = false;
    if (allParams.id) {
      // if the id is present, prepare params for rescheduling, no type is needed
      allParams.reschedule = allParams.id;
      allParams.id = undefined;
      rewriteSuccess = true;
    } else if (allParams.url) {
      // if the url is present, prepare params for scheduling, type is needed
      try {
        allParams.type = getAppointmentTypeFromAperoLink(allParams.url);
      } catch (error) {
        reportErrorSentry(error as Error);
      }
      rewriteSuccess = true;
    } else {
      reportErrorSentry(new Error("No id or url found for scheduler rewrite"));
    }

    if (rewriteSuccess) {
      // update and remove unused params
      allParams.popup = AppPopup.scheduleAppointment;
      allParams.url = undefined;
    }

    // do nothing if rewrite failed
  };

  /**
   * Patch query params for special popup types (e.g., apero links)
   * @param urlSearchParams The URLSearchParams to patch
   * @returns A new URLSearchParams with any necessary rewrites
   */
  patchQueryParams = (urlSearchParams: URLSearchParams): URLSearchParams => {
    const allParams: Record<string, string | undefined> = Object.fromEntries(
      urlSearchParams.entries()
    );
    const url = decodeURIComponent(allParams.url ?? "");

    // Rewrite iframe popup with apero link to in-app scheduler
    if (allParams.popup === AppPopup.iframe && isAperoLink(url)) {
      this.rewriteToScheduler(allParams);
    }

    // Build a new object omitting keys with undefined values
    const filteredParams: Record<string, string> = {};
    Object.keys(allParams).forEach((key) => {
      const value = allParams[key];
      if (typeof value === "string") {
        filteredParams[key] = value;
      }
    });

    return new URLSearchParams(filteredParams);
  };

  /**
   * Open a popup with the given options.
   * @param popup The popup type to open
   * @param options Additional options for opening the popup
   */
  openPopup = (popup: AppPopup, options: AppQueryPopupOpenOptions = {}) => {
    const { replace = false, additionalParameters = {} } = options;

    // update event listeners
    this.eventListeners = Object.keys(options.onEvent ?? {}).map((event) => ({
      event,
      callback: options.onEvent?.[event]
    }));
    this.addCloseListener(popup);

    const params = new URLSearchParams();

    Object.keys(additionalParameters).forEach((key) => {
      params.set(key, additionalParameters[key]);
    });

    if (options.zIndex) {
      params.set("z", options.zIndex.toString());
    }
    params.set("popup", popup);

    this.updateQueryParams(params, replace);

    this.openedByMethod = popup;
  };

  handleQuestionnaireSaved = () => {
    if (this.currentDialogStayDisabled) {
      this.closePopup();
    }
  };

  /**
   * Add close and custom event listeners for the popup.
   * Prevents duplicate listeners.
   * @param popup The popup type
   */
  addCloseListener = (popup: AppPopup) => {
    document.removeEventListener(
      "nineQuestionnaireSaved",
      this.handleQuestionnaireSaved
    );

    if (popup === AppPopup.questionnaire) {
      document.addEventListener(
        "nineQuestionnaireSaved",
        this.handleQuestionnaireSaved,
        { once: true }
      );
    }

    // add event listeners for current popup instance, prevent duplicates
    this.eventListeners.forEach(({ event, callback }) => {
      if (event && callback) {
        document.removeEventListener(event, callback); // ensure no duplicate
        document.addEventListener(event, callback, { once: true });
      }
    });
  };

  closePopup = ({
    dismissModal = true
  }: {
    dismissModal?: boolean;
  } = {}) => {
    if (!this.state.popup) {
      return;
    }
    this.patch({ popup: null });

    if (dismissModal) {
      void this.dismissModal?.dismiss();
    }

    document.dispatchEvent(new CustomEvent("popupClosed", { bubbles: true }));
    this.removeEventListeners();

    this.useNavigate("?");
  };

  /**
   * Remove all custom event listeners for the popup.
   */
  removeEventListeners = () => {
    this.eventListeners.forEach(({ event, callback }) => {
      if (event && callback) {
        document.removeEventListener(event, callback);
      }
    });
  };

  updateQueryParams = (urlSearchParams: URLSearchParams, replace = false) => {
    const patchedParams = this.patchQueryParams(urlSearchParams);
    const currentQueryParams = new URLSearchParams(window.location.search);

    const hasChanged = Array.from(patchedParams.entries()).some(
      ([key, value]) => {
        return currentQueryParams.get(key) !== value;
      }
    );

    if (!hasChanged) {
      return;
    }

    this.useNavigate(`?${patchedParams.toString()}`, { replace });
  };

  currentDialogStayDisabled = false;
  setCurrentQuery = (urlSearchParams: URLSearchParams) => {
    const params = this.patchQueryParams(urlSearchParams);

    const popup = params.get("popup") ?? "";
    const stay = params.get("stay") ?? "true";

    if (popup === this.state.popup) {
      return;
    }
    this.updateQueryParams(params, true);
    const closed = this.state.popup && popup === "";
    this.currentDialogStayDisabled = stay === "false";

    if (closed) {
      // call all onClose callbacks
      this.callbacks.onClose.forEach((callback) => callback(popup));
      this.removeEventListeners();
    }

    if (popup) {
      this.patch({ popup: popup as AppPopup });
      appStatusBarState.setThemeDark();
      tracker.track("App Popup Opened", {
        data: {
          "Popup ID": popup
        }
      });
    } else {
      this.patch({ popup: null });
      void this.dismissModal?.dismiss();
      appStatusBarState.resetThemeDefault();
    }

    if (!this.openedByMethod && stay === "false") {
      this.closePopup();
      return;
    }
  };

  dismissModal: HTMLIonModalElement | null = null;
  setModalDismiss = (dismiss: typeof this.dismissModal) => {
    this.dismissModal = dismiss;
  };
}

export const AppQueryPopupsController = Blac.getBloc(AppQueryPopupsBloc);

/**
 * Utility Hooks
 */
export type useAppQueryPopupOpenMethod = (
  options?: AppQueryPopupOpenOptions
) => void;
export type useAppQueryPopupReturnType = [
  useAppQueryPopupOpenMethod,
  () => void
];

export const useAppQueryPopup = (
  popup: AppPopup,
  options: {
    onClose?: () => unknown;
  } = {}
): useAppQueryPopupReturnType => {
  return [
    (o?: AppQueryPopupOpenOptions) => {
      AppQueryPopupsController.openPopup(popup, {
        ...o,
        onEvent: {
          popupClosed: () => {
            options.onClose?.();
          },
          ...o?.onEvent
        }
      });
    },
    () => AppQueryPopupsController.closePopup()
  ];
};
